/**
 * The site's theme: light, dark, or the system's. The choice is kept per browser
 * (`app.theme`); unset means the system's. index.html applies the same rule in <head> before
 * the first paint, so a page never flashes the other theme; this module keeps <html> in step
 * after that (the `dark` class the tokens hang on, `color-scheme` for form controls and
 * scrollbars, and the browser's theme-color).
 */
export const THEME_KEY = 'app.theme';
export const THEMES = ['light', 'dark', 'system'];

const storage = () => {
  try { return window.localStorage; } catch { return null; }
};

/** ?theme=light|dark on the URL: one look at this page in that theme, never kept. */
const askedTheme = () => {
  if (typeof window === 'undefined') return null;
  const m = /[?&]theme=(light|dark)\b/.exec(window.location.search);
  return m ? m[1] : null;
};

/** What the reader chose: 'light', 'dark', or 'system' when they chose nothing. */
export function readThemeChoice() {
  const asked = askedTheme();
  if (asked) return asked;
  const v = storage()?.getItem(THEME_KEY);
  return v === 'light' || v === 'dark' ? v : 'system';
}

export const systemPrefersDark = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

/** The theme a choice comes out as on this device. */
export const resolveTheme = (choice) => (choice === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : choice);

/** Puts a resolved theme on the document. */
export function applyTheme(resolved) {
  if (typeof document === 'undefined') return;
  const dark = resolved === 'dark';
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
  root.dataset.theme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', dark ? '#000000' : '#ffffff');
}

/** Keeps a choice, and applies it. */
export function saveThemeChoice(choice) {
  const s = storage();
  if (s) {
    if (choice === 'system') s.removeItem(THEME_KEY);
    else s.setItem(THEME_KEY, choice);
  }
  applyTheme(resolveTheme(choice));
}
